import { Component, OnDestroy, OnInit } from '@angular/core';
import { CatalogCardModel, CatalogMeModel, CatalogSectionModel, CompositeProductModel, CompositeProductType, HxCatalogService, HxProductInfoService, HxUiLabelPipe, SubProductModel } from 'hx-services';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { provideTranslocoScope, TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SubProductEditComponent } from '@adm-web-app/modules/composite-products/subproduct-edit/subproduct-edit.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { produce } from 'immer';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderSubtitleDirective, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxCountrySelectComponent, HxLabelEditComponent } from 'hx-components';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NgFor } from '@angular/common';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { CatalogLabelSelectComponent } from '@adm-web-app/modules/composite-products/labels/label-select.component';
import { CatalogMeService } from '@adm-web-app/modules/composite-products/catalog/catalog-me.service';
import { CatalogNotifyService } from '@adm-web-app/modules/composite-products/catalog/catalog-notify.service';
import { labelText } from '@adm-web-app/modules/composite-products/catalog/catalog-text';

@Component({
    selector: 'hc-composite-products-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.less'],
  providers: [provideTranslocoScope('composite-products')],
  imports: [TranslocoDirective, NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzPageHeaderSubtitleDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxLabelEditComponent, HxCountrySelectComponent, NzSelectComponent, NgFor, NzOptionComponent, NzInputNumberComponent, NzRadioGroupComponent, NzRadioComponent, CatalogLabelSelectComponent, NzInputDirective, NzDividerComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzPopconfirmDirective, HxUiLabelPipe, TranslocoPipe]
})
export class CompositeProductEditComponent implements OnInit, OnDestroy {
  product!: CompositeProductModel;
  isEdit = false;
  isCreate = false;
  isSaving = false;
  form!: UntypedFormGroup;
  subProducts: SubProductModel[] = [];

  compositeTypeOptions = Object.keys(CompositeProductType).map(key => ({key: `enum.CompositeProductType.${key}`, value: key}));
  currency?: string;
  /** what the signed-in employee may do: the section is put by the admin only */
  me: CatalogMeModel = {admin: false, canEditCards: false, canCreateCards: false, cardRightsEnforced: false};
  /** the sections of the country of the card (a draft is offered too: the card can be put there before the section is published) */
  sectionOptions: { id: number, text: string }[] = [];
  /** the address of the page of the cake, read only; empty while it is not issued */
  slug = '';
  private sections: CatalogSectionModel[] = [];
  private sectionOfCard?: number;
  private $destroyed = new Subject<void>();

  constructor(
    private aRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private modal: NzModalService,
    private productService: HxProductInfoService,
    private tr: TranslocoService,
    private notification: NzNotificationService,
    private router: Router,
    private catalog: HxCatalogService,
    private meService: CatalogMeService,
    private notify: CatalogNotifyService,
  ) {
  }

  breadcrumbFn: (key: string) => string = key => this.tr.translate(`breadcrumb.${key}`);

  ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.product = routeData['product'] as CompositeProductModel;
    if (this.product) {
      this.isEdit = true;
    } else {
      this.product = {} as CompositeProductModel;
      this.isCreate = true;
    }

    this.form = this.fb.group({
      countryId: [null, [Validators.required]],
      title: [null, [Validators.required]],
      description: [null, [Validators.required]],
      type: [CompositeProductType.SINGLE, [Validators.required]],
      sortOrder: [null],
      price: [null, [Validators.required]],
      onlineSellable: [false, [Validators.required]],
      sellable: [false, [Validators.required]],
      tags: [null],
      // not a field of the product: the section lives in the catalog and is saved by its own request
      sectionId: [null],
    });
    this.form.patchValue(this.product);
    this.subProducts = this.product.subProducts || [];
    void this.initCatalog();
  }

  addSubProduct() {
    this.modal.create({
      nzTitle: 'Продукт',
      nzContent: SubProductEditComponent,
      nzFooter: null
    }).afterClose.subscribe(result => {
      if (result) {
        this.subProducts = produce(this.subProducts, subProducts => {
          subProducts.push(result as SubProductModel);
        });
      }
    });
  }

  editSubProduct(subProduct: SubProductModel, index: number) {
    const modalRef = this.modal.create({
      nzTitle: 'Продукт',
      nzContent: SubProductEditComponent,
      nzFooter: null
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.subProduct = subProduct;
    }
    modalRef.afterClose.subscribe(result => {
      if (result) {
        this.subProducts = produce(this.subProducts, supProducts => {
          supProducts[index] = result;
        });
      }
    });
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid && !this.isSaving) {
      const {sectionId, ...fields} = this.form.getRawValue() as Record<string, unknown> & { sectionId: number | null };
      const model = fields as unknown as CompositeProductModel;
      model.id = this.product.id;
      model.subProducts = this.subProducts;
      this.isSaving = true;
      let savedId: number;
      try {
        savedId = (await firstValueFrom(this.productService.saveCompositeProduct(model))).id;
      } catch (err) {
        this.isSaving = false;
        this.notify.error(err);
        return;
      }
      this.notification.create(
        'success',
        'Композитный продукт',
        this.tr.translate('nt.dataSaved')
      );
      // the section is saved after the card: a new card has no id before, and the address is issued for the card as it is saved
      const sectionFailed = await this.saveSection(savedId, sectionId);
      this.isSaving = false;
      if (this.isCreate) {
        this.router.navigate([`/composite-products/${savedId}/edit`]);
      } else if (!sectionFailed) {
        void this.showCardInfo(savedId);
      }
    }
  }

  deleteSubProduct(ix: number) {
    this.subProducts.splice(ix, 1);
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  /** The section of the card and its address come from the catalog; the list of sections follows the country of the form. */
  private async initCatalog() {
    this.meService.me().then(me => {
      this.me = me;
      if (!me.admin) {
        this.form.controls['sectionId'].disable();
      }
    });
    this.form.controls['countryId'].valueChanges.pipe(takeUntil(this.$destroyed)).subscribe(countryId => void this.loadSections(countryId));
    if (this.isEdit) {
      await this.showCardInfo(this.product.id);
    }
    await this.loadSections(this.form.controls['countryId'].value);
  }

  private async showCardInfo(id: number) {
    try {
      const card: CatalogCardModel = await this.catalog.getCardById(id);
      this.sectionOfCard = card.sectionId;
      this.slug = card.slug ?? '';
      this.form.controls['sectionId'].setValue(card.sectionId ?? null, {emitEvent: false});
      this.form.controls['sectionId'].markAsPristine();
    } catch {
      // the card is shown without its section and address; the product itself is edited as before
    }
  }

  private async loadSections(countryId?: number | null) {
    if (!countryId) {
      this.sectionOptions = [];
      return;
    }
    try {
      this.sections = await this.catalog.getSections({countryId});
    } catch {
      // the sections did not come: the section of the card stays as it is, so a save does not take the card out of it
      return;
    }
    const lang = this.tr.getActiveLang();
    const draft = this.tr.translate('compositeProducts.sections.draft');
    this.sectionOptions = this.sections.map(s => ({id: s.id, text: s.enabled ? labelText(lang, s.title) : `${labelText(lang, s.title)} (${draft})`}));
    const chosen = this.form.controls['sectionId'].value as number | null;
    if (chosen !== null && !this.sections.some(s => s.id === chosen)) {
      // the country changed: a section of another country does not fit
      this.form.controls['sectionId'].setValue(null);
    }
  }

  /** Puts the card into the chosen section (or out of any) when that is a change; true when the request failed. */
  private async saveSection(cardId: number, sectionId: number | null): Promise<boolean> {
    if (!this.me.admin || (sectionId ?? undefined) === this.sectionOfCard) {
      return false;
    }
    try {
      await this.catalog.setCardSection(cardId, sectionId ?? undefined);
      this.sectionOfCard = sectionId ?? undefined;
      return false;
    } catch (err) {
      this.notify.error(err);
      return true;
    }
  }
}
